import React, { useState } from "react";
import {
  WidthContainer,
  TextInput,
  Button,
  IntersectionAnimation,
  SliceConfig
} from "~components";
import regex from "~constants/regex";
import cn from "classnames";
import * as styles from "./styles.module.scss";

const ContactBlock = ({
  data: {
    renderIndex,
    header,
    subheader,
    formOnLeft,
    form,
    successHeader,
    successMessage,
    sliceConfig
  }
}) => {
  const FORMSPARK_ENDPOINT = `https://api.formspark.io`;
  const FORMSPARK_ID = `J8NeUk37`;

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const formFields = [`name`, `email`, `subject`, `message`, `honeypot`];
  const initialFormValue = {};
  const initialFormErrors = {};
  for (const field of formFields) {
    initialFormValue[field] = ``;
    initialFormErrors[field] = false;
  }

  const [formValue, setFormValue] = useState(initialFormValue);
  const [formErrors, setFormErrors] = useState(initialFormErrors);

  const handleBlur = (field) => {
    if (!regex[field].test(formValue[field])) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: true
      }));
    }
  };

  const formHasError = () => {
    const relevantFormFields = formFields.filter(
      (field) => field !== `honeypot`
    );

    for (const field of relevantFormFields) {
      if (!formValue[field]) return true;
      if (formErrors[field]) return true;
    }
    return false;
  };

  const isButtonDisabled = () => {
    if (hasSubmitted || isSubmitting || formHasError()) return true;

    return false;
  };

  const handleUpdateValue = (value, formField) => {
    setHasError(false);
    setFormValue((prev) => ({ ...prev, [formField]: value }));
    setFormErrors((prev) => ({ ...prev, [formField]: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formHasError()) return;
    if (formValue.honeypot) return;
    setHasError(false);
    setIsSubmitting(true);

    try {
      const response = await fetch(`${FORMSPARK_ENDPOINT}/${FORMSPARK_ID}`, {
        method: `POST`,
        headers: {
          "Content-Type": `application/json`,
          Accept: `application/json`
        },
        body: JSON.stringify({
          name: formValue.name,
          email: formValue.email,
          subject: formValue.subject,
          message: formValue.message
        })
      });

      const data = await response.json();

      if (data.statusCode === 500) {
        throw new Error(`Something went wrong submitting form: ${data.body}`);
      }

      setHasSubmitted(true);
    } catch (error) {
      console.error(error);
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const Header = renderIndex === 0 ? `h1` : `h2`;

  return (
    <article>
      <SliceConfig config={sliceConfig}>
        <WidthContainer>
          <div
            className={cn(styles.flexWrapper, {
              [styles.formOnLeft]: formOnLeft
            })}
          >
            <div className={styles.text}>
              <IntersectionAnimation animation="fadeGrow">
                <Header className={cn(styles.header, `d2`)}>{header}</Header>
              </IntersectionAnimation>
              <IntersectionAnimation animation="fadeGrow" delay={100}>
                <p className="b1">{subheader}</p>
              </IntersectionAnimation>
            </div>
            <div className={styles.formContainer}>
              <form aria-hidden={hasSubmitted} onSubmit={handleSubmit}>
                <div className={styles.nameAndEmailFields}>
                  {/* Name */}
                  <TextInput
                    className={styles.inputField}
                    required
                    label={form[formFields[0]].label}
                    placeholder={form[formFields[0]].placeholder}
                    onChange={(value) =>
                      handleUpdateValue(value, formFields[0])
                    }
                    value={formValue[formFields[0]]}
                    hasError={formErrors[formFields[0]]}
                    disabled={hasSubmitted}
                    onBlur={() => handleBlur(formFields[0])}
                  />
                  {/* Email */}
                  <TextInput
                    className={styles.inputField}
                    required
                    label={form[formFields[1]].label}
                    placeholder={form[formFields[1]].placeholder}
                    onChange={(value) =>
                      handleUpdateValue(value, formFields[1])
                    }
                    value={formValue[formFields[1]]}
                    hasError={formErrors[formFields[1]]}
                    disabled={hasSubmitted}
                    onBlur={() => handleBlur(formFields[1])}
                  />
                </div>
                {/* Subject */}
                <TextInput
                  className={styles.inputField}
                  label={form[formFields[2]].label}
                  placeholder={form[formFields[2]].placeholder}
                  onChange={(value) => handleUpdateValue(value, formFields[2])}
                  value={formValue[formFields[2]]}
                  hasError={formErrors[formFields[2]]}
                  required
                  disabled={hasSubmitted}
                  onBlur={() => handleBlur(formFields[2])}
                />
                {/* Message */}
                <TextInput
                  className={styles.inputField}
                  required
                  label={form[formFields[3]].label}
                  placeholder={form[formFields[3]].placeholder}
                  onChange={(value) => handleUpdateValue(value, formFields[3])}
                  value={formValue[formFields[3]]}
                  hasError={formErrors[formFields[3]]}
                  textarea
                  disabled={hasSubmitted}
                  onBlur={() => handleBlur(formFields[3])}
                />

                {/* Honeypot */}
                <input
                  onChange={(e) =>
                    handleUpdateValue(e.target.value, formFields[4])
                  }
                  value={formValue[formFields[4]]}
                  className={styles.abc}
                  name="abc"
                />

                <div className={styles.submitButtonContainer}>
                  <Button
                    disabled={isButtonDisabled()}
                    buttonType="submit"
                    loading={isSubmitting}
                  >
                    Submit
                  </Button>
                  {hasError && (
                    <p className={cn(`caption`, styles.errorMessage)}>
                      Something went wrong!
                    </p>
                  )}
                </div>
              </form>
              <div
                aria-hidden={!hasSubmitted}
                className={cn(styles.successContainer, {
                  [styles.visible]: hasSubmitted
                })}
              >
                <h3 className="h2">{successHeader}</h3>
                <p className="b2">{successMessage}</p>
                <Button disabled={!hasSubmitted} to="/">
                  Back to home
                </Button>
              </div>
            </div>
          </div>
        </WidthContainer>
      </SliceConfig>
    </article>
  );
};

export default ContactBlock;
